/**
 * Buyer-Demand Phase 1 / Phase 5 commit 1.5 — admin buyers list +
 * create modal.
 *
 * The legacy per-buyer HMAC secret was retired in Phase 5 commit 1
 * (verifier removed) and the admin-side generation / reveal flow was
 * removed in commit 1.5. Create now just records buyer metadata and
 * closes — no secret-reveal step.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  fetchBuyers,
  createBuyer,
} from '../../lib/adminApi';

export default function AdminBuyers() {
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'buyers'],
    queryFn: fetchBuyers,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold font-display text-white">Buyers</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" /> New buyer
        </button>
      </div>

      <div className="bg-slate-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-slate-400 text-sm">Loading…</div>
        ) : !data?.buyers?.length ? (
          <div className="p-6 text-slate-400 text-sm">
            No buyers yet. Create the first one — TestSet — to start onboarding direct demand.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Projects</th>
                <th className="text-left px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.buyers.map((b) => (
                <tr key={b.id} className="border-t border-slate-700 hover:bg-slate-700/40">
                  <td className="px-4 py-3">
                    <Link to={`/admin/buyers/${b.id}`} className="text-white font-medium hover:underline">
                      {b.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    <div>{b.contact_email}</div>
                    {b.contact_name && <div className="text-xs text-slate-500">{b.contact_name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      b.status === 'active' ? 'bg-green-900/50 text-green-400' :
                      b.status === 'paused' ? 'bg-amber-900/50 text-amber-400' :
                      'bg-slate-700 text-slate-400'
                    }`}>{b.status}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{b._count?.exchange_surveys ?? 0}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {new Date(b.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateBuyerModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

function CreateBuyerModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createBuyer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'buyers'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl p-6 w-full max-w-md space-y-4">
        <h2 className="text-lg font-bold text-white">New buyer</h2>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Name (e.g. TestSet)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
          />
          <input
            type="email"
            placeholder="Contact email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
          />
          <input
            type="text"
            placeholder="Contact name (optional)"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
          />
          <textarea
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
          />
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 text-slate-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              mutation.mutate({
                name,
                contact_email: email,
                contact_name: contactName || undefined,
                notes: notes || undefined,
              })
            }
            disabled={!name || !email || mutation.isPending}
            className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating…' : 'Create buyer'}
          </button>
        </div>
      </div>
    </div>
  );
}
